import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Navigation } from '@/components/Navigation';
import { Loader2, Heart, Film } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { TrialExpiredModal } from '@/components/TrialExpiredModal';
import { MovieCard } from '@/components/MovieCard';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Favorites() {
  const [movies, setMovies] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchFavorites();
  }, [user, token, authLoading, navigate]);

  const fetchFavorites = async () => {
    if (!token) return;
    try {
      setDataLoading(true);
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMovies(response.data.movies || []);
    } catch (e) {
    } finally {
      setDataLoading(false);
    }
  };

  const handleFavoriteToggle = () => {
    fetchFavorites();
  };

  const movieFavoriteIds = movies.map(m => m.id);

  return (
    <div className="min-h-screen bg-bg-primary">
      <TrialExpiredModal />
      <Navigation />
      
      <div className="pt-24 px-4 md:px-8 lg:px-12 xl:px-16 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bebas font-bold text-white mb-2 flex items-center gap-3">
            <Heart className="w-10 h-10 text-netflix-red fill-netflix-red" />
            MES FAVORIS
          </h1>
          <p className="text-text-secondary font-inter text-sm md:text-base">
            Tous vos films préférés en un seul endroit
          </p>
        </div>

        {authLoading || dataLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-netflix-red animate-spin" />
          </div>
        ) : (
          <>
            {movies.length > 0 ? (
              <div className="mb-12">
                <h2 className="text-2xl md:text-3xl font-bebas font-bold text-white mb-6 flex items-center gap-2">
                  <Film className="w-7 h-7 text-netflix-red" />
                  Films ({movies.length})
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {movies.map((movie) => (
                    <MovieCard 
                      key={movie.id} 
                      movie={movie}
                      isFavorite={movieFavoriteIds.includes(movie.id)}
                      onFavoriteToggle={handleFavoriteToggle}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Heart className="w-20 h-20 text-text-tertiary mb-4" />
                <h2 className="text-2xl font-bebas text-white mb-2">
                  Aucun favori pour le moment
                </h2>
                <p className="text-text-secondary font-inter mb-6 max-w-md">
                  Ajoutez vos films préférés en cliquant sur le coeur sur n'importe quel film
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-netflix-red hover:bg-netflix-red-hover text-white font-work font-semibold rounded-md transition-colors"
                >
                  Découvrir des Films
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
